import {ReactElement, useMemo} from 'react';
import parse, {Element} from 'html-react-parser';

import classNames from 'classnames';
import ProductTile from '../ProductTile.client';
import HighlightBlock from '../HighlightBlock.client';
import {NewsArticleDetailType} from '../../types';
import {Product} from '@shopify/hydrogen/storefront-api-types';

interface ParsedNewsArticleContentProps {
  newsArticle: NewsArticleDetailType;
  embeddedProducts: ReadonlyArray<Product>;
}

export default function ParsedNewsArticleContent({
  newsArticle,
  embeddedProducts,
}: ParsedNewsArticleContentProps): ReactElement {
  const totalEmbeddedProducts = useMemo(
    () => embeddedProducts.length,
    [embeddedProducts],
  );
  const totalEmbeddedHighlights = newsArticle.embed_highlights.length;

  return (
    <div
      className={classNames('text-content', {
        'text-has-one-product': totalEmbeddedProducts > 0, // style used for > 1 as well
      })}
    >
      {parse(newsArticle.content, {
        replace: (domNode: Element) => {
          // -- embed highlights
          const match = domNode?.attribs?.id?.match(
            /embed_highlights-([0-9]+)/,
          );
          if (match && totalEmbeddedHighlights) {
            const highlightNumber = parseInt(match[1]);
            if (highlightNumber < totalEmbeddedHighlights) {
              const highlight = newsArticle.embed_highlights[highlightNumber];
              return (
                <>
                  <br />
                  <HighlightBlock key={highlight.title} highlight={highlight} />
                  <br />
                </>
              );
            }
          }

          // -- embed products
          if (
            domNode?.attribs?.id === 'embed_products' &&
            totalEmbeddedProducts > 0
          ) {
            return (
              <div
                className={classNames({
                  'product-box': totalEmbeddedProducts === 1,
                  'products-list products-list-3': totalEmbeddedProducts > 1,
                })}
              >
                {embeddedProducts.map((product) => (
                  <ProductTile key={product.id} product={product} />
                ))}
              </div>
            );
          }
        },
      })}
    </div>
  );
}
